import { secp256k1 } from "@noble/curves/secp256k1.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { hex } from "@scure/base";
import { equalBytes } from "@scure/btc-signer/utils.js";
import { BigNumber } from "bignumber.js";
import {
    Scripts,
    SwapTreeSerializer,
    type Types,
    compareTrees,
    reverseSwapTree,
    swapTree,
} from "boltz-core";
import type { ChainSwapDetails } from "boltz-swaps/client";
import { createAssetProvider } from "boltz-swaps/evm";
import { decodeInvoice } from "boltz-swaps/invoice";
import { AssetKind, SwapType } from "boltz-swaps/types";
import { createMusig, tweakMusig } from "boltz-swaps/utxo";
import { type Address, keccak256 } from "viem";

import {
    type AssetType,
    BTC,
    LBTC,
    getKindForAsset,
    isEvmAsset,
} from "../consts/Assets";
import { Denomination, Side } from "../consts/Enums";
import type { deriveKeyFn } from "../context/Global";
import { erc20SwapCodeHashes, etherSwapCodeHashes } from "../context/Web3";
import { decodeAddress } from "./compat";
import { formatAmountDenomination } from "./denomination";
import type { ECKeys } from "./ecpair";
import { isInvoice, isLnurl } from "./invoice";
import type {
    ChainSwap,
    ReverseSwap,
    SomeSwap,
    SubmarineSwap,
} from "./swapCreator";

// TODO: sanity check timeout block height?
// TODO: buffers for amounts

const invalidSendAmountMsg = (expected: number, got: number) =>
    `invalid send amount. Expected ${expected}, got ${got}`;
const invalidReceiveAmountMsg = (expected: number, got: number) =>
    `invalid receive amount. Expected ${expected} to be bigger than ${got}`;

type ContractGetter = (asset: string) => { address: Address };

const validateContract = async (
    getEtherSwap: ContractGetter,
    getErc20Swap: ContractGetter,
    asset: string,
): Promise<void> => {
    const isEtherSwap = getKindForAsset(asset) === AssetKind.EVMNative;

    const codeHashes = isEtherSwap
        ? etherSwapCodeHashes()
        : erc20SwapCodeHashes();
    if (codeHashes === undefined) {
        return;
    }

    const contract = (isEtherSwap ? getEtherSwap(asset) : getErc20Swap(asset))
        .address;
    if (contract === undefined) {
        throw new Error(`missing contract address for asset: ${asset}`);
    }
    const code = await createAssetProvider(asset).getCode({
        address: contract,
    });
    if (code === undefined || code === "0x") {
        throw new Error(`no deployed contract code found for asset: ${asset}`);
    }

    const hash = keccak256(code);

    if (!codeHashes.includes(hash)) {
        throw new Error(`invalid contract code hash: ${hash}`);
    }
};

const validateAddress = (
    chain: string,
    tree: Types.SwapTree,
    ourKeys: ECKeys,
    theirPublicKey: Uint8Array,
    address: string,
    blindingKey: string | undefined,
): void => {
    const keyAgg = createMusig(ourKeys, theirPublicKey);
    const tweaked = tweakMusig(chain, keyAgg, tree.tree);

    const compareScript = Scripts.p2trOutput(tweaked.aggPubkey);
    const decodedAddress = decodeAddress(chain, address);

    if (!equalBytes(decodedAddress.script, compareScript)) {
        throw new Error("decoded address script mismatch");
    }

    if (chain === LBTC) {
        if (!blindingKey) {
            throw new Error("missing blindingKey for LBTC address validation");
        }
        const blindingPrivateKey = hex.decode(blindingKey);
        const blindingPublicKey = secp256k1.getPublicKey(blindingPrivateKey);

        if (decodedAddress.blindingKey === undefined) {
            throw new Error("address is missing blinding key");
        }
        if (!equalBytes(decodedAddress.blindingKey, blindingPublicKey)) {
            throw new Error("blinding public key mismatch");
        }
    }
};

const validateBip21 = (
    bip21: string,
    address: string,
    expectedAmount: number,
): void => {
    const bip21Split = bip21.split("?");
    if (bip21Split[0].split(":")[1] !== address) {
        throw new Error("invalid BIP21 format");
    }

    const params = new URLSearchParams(bip21Split[1]);

    if (expectedAmount === 0) {
        const hasAmount = params.has("amount");
        if (hasAmount) {
            throw new Error(
                `unexpected amount in BIP21. Expected 0, got ${params.get("amount")}`,
            );
        }
        return;
    }

    if (
        params.get("amount") !==
        formatAmountDenomination(
            BigNumber(expectedAmount),
            Denomination.Btc,
            ".",
            BTC,
        )
    ) {
        throw new Error(
            `invalid BIP21 amount. Expected ${expectedAmount}, got ${params.get("amount")}`,
        );
    }
};

const validateReverse = async (
    swap: ReverseSwap,
    deriveKey: deriveKeyFn,
    getEtherSwap: ContractGetter,
    getErc20Swap: ContractGetter,
): Promise<void> => {
    const invoiceData = decodeInvoice(swap.invoice);

    // Amounts
    if (invoiceData.satoshis !== swap.sendAmount) {
        throw new Error(
            invalidSendAmountMsg(invoiceData.satoshis, swap.sendAmount),
        );
    }

    if (swap.onchainAmount <= swap.receiveAmount) {
        throw new Error(
            invalidReceiveAmountMsg(swap.onchainAmount, swap.receiveAmount),
        );
    }

    // Invoice
    const preimageHash = sha256(hex.decode(swap.preimage));
    if (invoiceData.preimageHash !== hex.encode(preimageHash)) {
        throw new Error(
            `invalid swap preimage hash. Expected ${hex.encode(preimageHash)}, got ${invoiceData.preimageHash}`,
        );
    }

    if (isEvmAsset(swap.assetReceive)) {
        await validateContract(getEtherSwap, getErc20Swap, swap.assetReceive);
        return;
    }

    // SwapTree
    const tree = SwapTreeSerializer.deserializeSwapTree(swap.swapTree);

    if (
        swap.claimPrivateKeyIndex === undefined ||
        swap.refundPublicKey === undefined
    ) {
        throw new Error("missing swap key data for reverse validation");
    }
    const ourKeys = deriveKey(
        swap.claimPrivateKeyIndex,
        swap.assetReceive as AssetType,
    );
    const theirPublicKey = hex.decode(swap.refundPublicKey);

    const compareTree = reverseSwapTree(
        swap.assetReceive === LBTC,
        preimageHash,
        ourKeys.publicKey,
        theirPublicKey,
        swap.timeoutBlockHeight,
    );

    if (!compareTrees(tree, compareTree)) {
        throw new Error("swap tree mismatch");
    }

    validateAddress(
        swap.assetReceive,
        tree,
        ourKeys,
        theirPublicKey,
        swap.lockupAddress,
        swap.blindingKey,
    );
};

const validateSubmarine = async (
    swap: SubmarineSwap,
    deriveKey: deriveKeyFn,
    getEtherSwap: ContractGetter,
    getErc20Swap: ContractGetter,
): Promise<void> => {
    // Amounts
    if (swap.expectedAmount !== swap.sendAmount) {
        throw new Error(
            invalidSendAmountMsg(swap.expectedAmount, swap.sendAmount),
        );
    }

    if (isEvmAsset(swap.assetSend)) {
        await validateContract(getEtherSwap, getErc20Swap, swap.assetSend);
        return;
    }

    // SwapTree
    const invoiceData = decodeInvoice(swap.invoice);

    const tree = SwapTreeSerializer.deserializeSwapTree(swap.swapTree);

    if (swap.refundPrivateKeyIndex === undefined) {
        throw new Error("missing refund key index for submarine validation");
    }
    const ourKeys = deriveKey(
        swap.refundPrivateKeyIndex,
        swap.assetSend as AssetType,
    );
    const theirPublicKey = hex.decode(swap.claimPublicKey);

    const compareTree = swapTree(
        swap.assetSend === LBTC,
        hex.decode(invoiceData.preimageHash),
        theirPublicKey,
        ourKeys.publicKey,
        swap.timeoutBlockHeight,
    );

    if (!compareTrees(tree, compareTree)) {
        throw new Error("swap tree mismatch");
    }

    // Address
    validateAddress(
        swap.assetSend,
        tree,
        ourKeys,
        theirPublicKey,
        swap.address,
        swap.blindingKey,
    );

    validateBip21(swap.bip21, swap.address, swap.expectedAmount);
};

const validateChainSwap = async (
    swap: ChainSwap,
    deriveKey: deriveKeyFn,
    getEtherSwap: ContractGetter,
    getErc20Swap: ContractGetter,
): Promise<void> => {
    const preimageHash = sha256(hex.decode(swap.preimage));

    const validateSide = async (
        side: Side,
        asset: string,
        details: ChainSwapDetails,
    ): Promise<void> => {
        if (side === Side.Send) {
            if (swap.sendAmount > 0 && details.amount !== swap.sendAmount) {
                throw new Error(
                    invalidSendAmountMsg(swap.sendAmount, details.amount),
                );
            }
        } else {
            if (
                swap.receiveAmount > 0 &&
                details.amount <= swap.receiveAmount
            ) {
                throw new Error(
                    invalidReceiveAmountMsg(swap.receiveAmount, details.amount),
                );
            }
        }

        if (isEvmAsset(asset)) {
            await validateContract(getEtherSwap, getErc20Swap, asset);
            return;
        }

        const keyIndex =
            side === Side.Send
                ? swap.refundPrivateKeyIndex
                : swap.claimPrivateKeyIndex;
        if (keyIndex === undefined) {
            throw new Error(
                `missing ${side === Side.Send ? "refund" : "claim"} key index for chain validation`,
            );
        }
        const ourKeys = deriveKey(keyIndex, asset as AssetType);
        const theirPublicKey = hex.decode(details.serverPublicKey);
        const tree = SwapTreeSerializer.deserializeSwapTree(details.swapTree);
        const compareTree = reverseSwapTree(
            asset === LBTC,
            preimageHash,
            side === Side.Send ? theirPublicKey : ourKeys.publicKey,
            side === Side.Send ? ourKeys.publicKey : theirPublicKey,
            details.timeoutBlockHeight,
        );

        if (!compareTrees(tree, compareTree)) {
            throw new Error("swap tree mismatch");
        }

        validateAddress(
            asset,
            tree,
            ourKeys,
            theirPublicKey,
            details.lockupAddress,
            details.blindingKey,
        );

        if (side === Side.Send) {
            if (details.bip21 === undefined) {
                throw new Error("missing bip21 for send-side chain validation");
            }
            validateBip21(details.bip21, details.lockupAddress, details.amount);
        }
    };

    await Promise.all([
        validateSide(Side.Send, swap.assetSend, swap.lockupDetails),
        validateSide(Side.Receive, swap.assetReceive, swap.claimDetails),
    ]);
};

export const validateResponse = async (
    swap: SomeSwap,
    deriveKey: deriveKeyFn,
    getEtherSwap: ContractGetter,
    getErc20Swap: ContractGetter,
): Promise<void> => {
    switch (swap.type) {
        case SwapType.Submarine:
            await validateSubmarine(
                swap as SubmarineSwap,
                deriveKey,
                getEtherSwap,
                getErc20Swap,
            );
            break;

        case SwapType.Reverse:
            await validateReverse(
                swap as ReverseSwap,
                deriveKey,
                getEtherSwap,
                getErc20Swap,
            );
            break;

        case SwapType.Chain:
            await validateChainSwap(
                swap as ChainSwap,
                deriveKey,
                getEtherSwap,
                getErc20Swap,
            );
            break;

        default:
            throw new Error("unknown_swap_type");
    }
};

export const validateInvoice = (inputValue: string): number => {
    const isInputInvoice = isInvoice(inputValue);
    if (isLnurl(inputValue) || isInputInvoice) {
        if (isInputInvoice) {
            let decoded: ReturnType<typeof decodeInvoice>;
            try {
                decoded = decodeInvoice(inputValue);
            } catch {
                throw new Error("invalid_invoice");
            }
            if (decoded.satoshis === 0) {
                throw new Error("invalid_0_amount");
            }
            return decoded.satoshis;
        }
    }
    throw new Error("invalid_invoice");
};
